import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode } from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { placedStyles } from "./select";
import { IconButton } from "./button";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  className?: string;
  /** Prevents closing by outside click (forms with unsaved data). */
  persistent?: boolean;
}

const modalSizes = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl", full: "max-w-6xl" };

export function Modal({ open, onClose, title, description, children, footer, size = "md", className, persistent }: ModalProps) {
  const { refs, context } = useFloating({ open, onOpenChange: (v) => !v && onClose() });
  const dismiss = useDismiss(context, { outsidePress: !persistent, outsidePressEvent: "mousedown" });
  const role = useRole(context, { role: "dialog" });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  if (!open) return null;
  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll className="z-[70] grid animate-fade-in place-items-end bg-[#02050c]/75 backdrop-blur-[2px] sm:place-items-center sm:p-4">
        <FloatingFocusManager context={context} initialFocus={-1}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className={cn(
              "flex max-h-[92dvh] w-full animate-pop-in flex-col overflow-hidden rounded-t-2xl border border-line-strong bg-panel shadow-pop sm:rounded-2xl",
              modalSizes[size],
              className,
            )}
          >
            {title || description ? (
              <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
                <div className="min-w-0">
                  {title ? <h2 className="text-base font-semibold text-ink">{title}</h2> : null}
                  {description ? <p className="mt-1 text-[13px] text-muted">{description}</p> : null}
                </div>
                <IconButton label="Fermer" size="sm" onClick={onClose}>
                  <X className="size-4" />
                </IconButton>
              </div>
            ) : null}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
            {footer ? <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-panel-2/40 px-5 py-3.5">{footer}</div> : null}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: "left" | "right";
  width?: string;
  className?: string;
  hideHeader?: boolean;
}

export function Drawer({ open, onClose, title, children, footer, side = "right", width = "w-[440px]", className, hideHeader }: DrawerProps) {
  const { refs, context } = useFloating({ open, onOpenChange: (v) => !v && onClose() });
  const dismiss = useDismiss(context, { outsidePressEvent: "mousedown" });
  const role = useRole(context, { role: "dialog" });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  if (!open) return null;
  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll className="z-[70] animate-fade-in bg-[#02050c]/70">
        <FloatingFocusManager context={context} initialFocus={-1}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className={cn(
              "fixed top-0 flex h-dvh max-w-[100vw] flex-col border-line-strong bg-panel shadow-pop",
              side === "right" ? "right-0 animate-slide-in-right border-l" : "left-0 animate-slide-in-left border-r",
              width,
              className,
            )}
          >
            {!hideHeader ? (
              <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
                <h2 className="truncate text-base font-semibold">{title}</h2>
                <IconButton label="Fermer" size="sm" onClick={onClose}>
                  <X className="size-4" />
                </IconButton>
              </div>
            ) : null}
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
            {footer ? <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">{footer}</div> : null}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}

export interface PopoverProps {
  trigger: ReactElement;
  children: ReactNode | ((close: () => void) => ReactNode);
  placement?: Placement;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Popover({ trigger, children, placement = "bottom-end", className, open: controlled, onOpenChange }: PopoverProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const setOpen = (v: boolean) => {
    setUncontrolled(v);
    onOpenChange?.(v);
  };
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    transform: false,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const close = () => setOpen(false);
  return (
    <>
      {isValidElement(trigger)
        ? cloneElement(trigger as ReactElement<Record<string, unknown>>, { ref: refs.setReference, ...getReferenceProps() })
        : trigger}
      {open ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={placedStyles(floatingStyles, isPositioned)}
            {...getFloatingProps()}
            className={cn("z-[75] rounded-xl border border-line-strong bg-[#0d1730] shadow-pop", isPositioned && "animate-pop-in", className)}
          >
            {typeof children === "function" ? children(close) : children}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  divider?: boolean;
  description?: string;
}

export function DropdownMenu({ trigger, items, placement = "bottom-end", width = "w-56" }: { trigger: ReactElement; items: MenuItem[]; placement?: Placement; width?: string }) {
  return (
    <Popover trigger={trigger} placement={placement} className={cn("p-1.5", width)}>
      {(close) => (
        <div role="menu">
          {items.map((item, i) =>
            item.divider ? (
              <div key={`d${i}`} className="my-1.5 h-px bg-line" />
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  close();
                  item.onSelect?.();
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors disabled:opacity-50",
                  item.danger ? "text-[#f87171] hover:bg-danger/10" : "text-ink hover:bg-panel-3",
                )}
              >
                {item.icon ? <span className="text-muted [&>svg]:size-4">{item.icon}</span> : null}
                <span className="min-w-0 flex-1">
                  <span className="block">{item.label}</span>
                  {item.description ? <span className="block text-[11.5px] text-muted">{item.description}</span> : null}
                </span>
              </button>
            ),
          )}
        </div>
      )}
    </Popover>
  );
}

export function Tooltip({ content, children, placement = "top" }: { content: ReactNode; children: ReactElement; placement?: Placement }) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    transform: false,
    middleware: [offset(6), flip(), shift({ padding: 6 })],
  });
  const hover = useHover(context, { delay: { open: 250, close: 0 } });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  return (
    <>
      {cloneElement(children as ReactElement<Record<string, unknown>>, { ref: refs.setReference, ...getReferenceProps() })}
      {open ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={placedStyles(floatingStyles, isPositioned)}
            {...getFloatingProps()}
            className="z-[90] max-w-xs rounded-lg border border-line-strong bg-[#121d36] px-2.5 py-1.5 text-xs text-ink shadow-pop"
          >
            {content}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}
